/**
 * Interactive Action Session — UI Session Pipeline
 *
 * Enables capabilities that require user interaction (modal dialogs,
 * panel editors, inline editors) to be invoked through a promise-based
 * session model.
 *
 * Architecture:
 * ┌─────────────┐       ┌──────────────────────┐       ┌─────────────┐
 * │  Caller      │──────▷│  InteractiveSession   │──────▷│  Backend    │
 * │  (Notes)     │       │  1. Show modal        │       │  signals    │
 * │              │◁──────│  2. Await user input   │◁──────│  only       │
 * │  auto-apply  │       │  3. Return result     │       │             │
 * └─────────────┘       └──────────────────────┘       └─────────────┘
 *
 * For interactive actions the pipeline is:
 *   1. Frontend detects `interaction !== 'none'` on the ActionDescriptor
 *   2. Frontend creates an InteractiveSession
 *   3. The session renders the appropriate UI (modal, panel, etc.)
 *   4. The user interacts and submits
 *   5. The session sends the result to the backend (POST /api/plugins/…)
 *      for signal emission and audit
 *   6. The backend response is returned to the original caller
 *
 * For headless actions, this module is bypassed entirely.
 *
 * Wave 23.2 — Interactive Capability Pipeline Fix
 */

import type { ActionDescriptor, ActionInteractionMode } from '../plugins/registries/action-registry';
import { useActionRegistryStore } from '../plugins/registries/action-registry';
import type { DeferredActionResponse } from '../types/plugin-interaction';
import { useDeferredActionStore } from '../plugins/deferred-action-registry';

// ── Types ──────────────────────────────────────────────────

/**
 * A pending interactive session — created when an interactive action
 * is invoked and resolved when the user completes the UI interaction.
 */
export interface InteractiveSession<TResult = unknown> {
  /** Unique session ID */
  id: string;
  /** The action descriptor that initiated this session */
  action: ActionDescriptor;
  /** The input content provided to the action */
  inputContent: string | Record<string, unknown>;
  /** The content shape of the input */
  contentShape: string;
  /** Metadata from the caller */
  metadata: Record<string, unknown>;
  /** Context from the caller */
  context?: {
    entityId?: string;
    entityType?: string;
    sourcePluginId?: string;
    [key: string]: unknown;
  };
  /** Resolve the session with a result */
  resolve: (result: TResult) => void;
  /** Reject the session (user cancelled or error) */
  reject: (reason?: string) => void;
  /** Timestamp when the session was created */
  createdAt: number;
}

/**
 * The result shape from an interactive modal session.
 */
export interface InteractiveModalResult {
  /** The edited/transformed content */
  content: string | Record<string, unknown>;
  /** Whether the user explicitly applied changes (vs auto-resolve) */
  applied: boolean;
  /** Optional metadata from the UI */
  metadata?: Record<string, unknown>;
}

/**
 * Listener that receives interactive session events.
 * The UI layer (InteractiveSessionProvider) subscribes to these events
 * and renders the appropriate UI.
 */
export type InteractiveSessionListener = (session: InteractiveSession<InteractiveModalResult>) => void;

// ── Session Manager ────────────────────────────────────────

let nextSessionId = 1;
const activeSessions = new Map<string, InteractiveSession<InteractiveModalResult>>();
const listeners = new Set<InteractiveSessionListener>();

/**
 * Check whether an action descriptor declares interactive requirements.
 */
export function isInteractiveAction(action: ActionDescriptor): boolean {
  const mode = action.interaction ?? 'none';
  return mode !== 'none';
}

/**
 * Get the interaction mode for an action (defaults to 'none').
 */
export function getInteractionMode(action: ActionDescriptor): ActionInteractionMode {
  return action.interaction ?? 'none';
}

/**
 * Subscribe to interactive session creation events.
 * Returns an unsubscribe function.
 */
export function onInteractiveSession(listener: InteractiveSessionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Create an interactive session for a capability invocation.
 *
 * This is the core of the interactive pipeline:
 * - Creates a promise that the caller can await
 * - Notifies all listeners (UI layer) to render the modal/panel
 * - Returns the promise that resolves when the user completes the interaction
 *
 * @param action - The action descriptor
 * @param inputContent - The content being sent to the action
 * @param contentShape - The content shape
 * @param metadata - Caller metadata
 * @param context - Caller context
 * @returns Promise that resolves with the interactive result
 */
export function createInteractiveSession(
  action: ActionDescriptor,
  inputContent: string | Record<string, unknown>,
  contentShape: string,
  metadata: Record<string, unknown>,
  context?: InteractiveSession['context'],
): Promise<InteractiveModalResult> {
  return new Promise<InteractiveModalResult>((resolve, reject) => {
    const id = `session-${nextSessionId++}-${Date.now()}`;

    const session: InteractiveSession<InteractiveModalResult> = {
      id,
      action,
      inputContent,
      contentShape,
      metadata,
      context,
      resolve: (result) => {
        activeSessions.delete(id);
        resolve(result);
      },
      reject: (reason) => {
        activeSessions.delete(id);
        reject(new Error(reason ?? 'Interactive session cancelled'));
      },
      createdAt: Date.now(),
    };

    activeSessions.set(id, session);

    // Notify all listeners (the UI provider will pick this up)
    for (const listener of listeners) {
      try {
        listener(session);
      } catch (err) {
        console.error('[InteractiveSession] Listener error:', err);
      }
    }
  });
}

/**
 * Get all currently active interactive sessions.
 */
export function getActiveSessions(): InteractiveSession<InteractiveModalResult>[] {
  return Array.from(activeSessions.values());
}

/**
 * Get a specific session by ID.
 */
export function getSession(id: string): InteractiveSession<InteractiveModalResult> | undefined {
  return activeSessions.get(id);
}

/**
 * Cancel all active sessions (e.g., on navigation or unmount).
 */
export function cancelAllSessions(reason = 'All sessions cancelled'): void {
  for (const session of activeSessions.values()) {
    session.reject(reason);
  }
  activeSessions.clear();
}

// ── Invocation Orchestrator ────────────────────────────────

export interface InvokeActionOptions {
  /** The action to invoke */
  action: ActionDescriptor;
  /** The content to send */
  content: string | Record<string, unknown>;
  /** Content shape (e.g., 'text.markdown') */
  contentShape: string;
  /** Caller metadata */
  metadata?: Record<string, unknown>;
  /** Caller context */
  context?: {
    entityId?: string;
    entityType?: string;
    sourcePluginId?: string;
    [key: string]: unknown;
  };
  /** API base URL (default: '') */
  apiBase?: string;
}

export interface InvokeActionResult {
  /** Content shape of the output */
  outputShape: string;
  /** The result content */
  content: string | Record<string, unknown>;
  /** Result metadata */
  metadata?: Record<string, unknown>;
  /** Whether this was from an interactive session */
  interactive: boolean;
}

/**
 * Unified action invocation that routes through the correct pipeline:
 *
 * - **Headless** (`interaction === 'none'`):
 *   POST /api/plugins/{pluginId}/actions/{actionName}
 *   Backend handler runs → returns result.
 *
 * - **Interactive** (`interaction === 'modal' | 'panel' | 'inline-edit'`):
 *   1. Create an interactive session (shows modal/panel)
 *   2. Wait for user interaction
 *   3. POST to backend with the user-edited content (for signal emission)
 *   4. Return result to caller
 *
 * This function replaces the direct fetch() calls in consumer plugins.
 */
export async function invokeAction(options: InvokeActionOptions): Promise<InvokeActionResult> {
  const {
    action,
    content,
    contentShape,
    metadata = {},
    context = {},
    apiBase = '',
  } = options;

  if (isInteractiveAction(action)) {
    return invokeInteractiveAction(options);
  }

  return invokeHeadlessAction(options);
}

/**
 * Headless action invocation — direct backend POST.
 */
async function invokeHeadlessAction(options: InvokeActionOptions): Promise<InvokeActionResult> {
  const { action, content, contentShape, metadata = {}, context = {}, apiBase = '' } = options;

  const response = await fetch(
    `${apiBase}/api/plugins/${action.pluginId}/actions/${action.name}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        contentShape,
        content,
        metadata,
        context,
      }),
    },
  );

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(
      errData.error ?? errData.message ?? `Action "${action.label}" failed (${response.status})`,
    );
  }

  const result = await response.json();
  return {
    outputShape: result.outputShape ?? action.produces,
    content: result.content,
    metadata: result.metadata,
    interactive: false,
  };
}

/**
 * Interactive action invocation — UI session pipeline.
 *
 * Wave 22.13.3: Now checks if the plugin registered a frontend handler
 * via actions.register() in setup(). If so, calls that handler directly
 * (the plugin manages its own modal lifecycle via PluginModalHost).
 * Falls back to the generic InteractiveSession modal if no handler exists.
 *
 * Plugin-handler path (edit-markdown, etc.):
 *   1. Call registered handler with { content, context }
 *   2. Handler calls actions.defer() + ui.openModal() → plugin-owned modal
 *   3. Handler resolves → POST to backend for signals → return result
 *
 * Generic-session path (no registered handler):
 *   1. Create InteractiveSession → InteractiveSessionProvider renders modal
 *   2. User edits → session resolves
 *   3. POST to backend for signals → return result
 */
async function invokeInteractiveAction(options: InvokeActionOptions): Promise<InvokeActionResult> {
  const { action, content, contentShape, metadata = {}, context = {}, apiBase = '' } = options;

  // ── Wave 22.13.3: Check for plugin-registered handler ──
  const registeredHandler = useActionRegistryStore.getState().getHandler(
    action.pluginId,
    action.name,
  );

  if (registeredHandler) {
    // Plugin owns the modal lifecycle (defer + openModal + events).
    // Call the handler with the same shape the plugin expects.
    const rawHandlerResult = await registeredHandler({
      content,
      context: {
        ...context,
        entityId: (context as any).entityId,
        sourcePluginId: (context as any).sourcePluginId,
      },
    });

    // ── Wave 22.13.3: Resolve deferred actions ──
    // Handlers that use actions.defer() return { deferred: true, actionId }
    // immediately while the modal runs asynchronously.  We must await the
    // deferred action's resolution to obtain the actual edited content.
    let handlerResult = rawHandlerResult;
    if (rawHandlerResult?.deferred === true && rawHandlerResult?.actionId) {
      const deferredResponse = rawHandlerResult as DeferredActionResponse;
      handlerResult = await useDeferredActionStore
        .getState()
        .waitFor(deferredResponse.actionId);
    }

    // Check for cancellation (either direct or from deferred resolution)
    if (handlerResult?.cancelled) {
      throw new Error('User cancelled the action');
    }

    // Post to backend for signal emission / observability
    try {
      const response = await fetch(
        `${apiBase}/api/plugins/${action.pluginId}/actions/${action.name}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            contentShape,
            content: handlerResult?.content ?? content,
            metadata: {
              ...metadata,
              interactiveSession: true,
              handlerManaged: true,
            },
            context,
          }),
        },
      );

      if (response.ok) {
        const backendResult = await response.json();
        return {
          outputShape: backendResult.outputShape ?? action.produces,
          content: backendResult.content ?? handlerResult?.content ?? content,
          metadata: {
            ...backendResult.metadata,
            interactive: true,
            handlerManaged: true,
          },
          interactive: true,
        };
      }
    } catch (signalErr) {
      // Signal emission failure is non-fatal — we still have the handler result
      console.warn(`[invokeAction] Signal emission failed for ${action.name}:`, signalErr);
    }

    // Return handler result directly if backend signal emission failed
    return {
      outputShape: handlerResult?.outputShape ?? action.produces,
      content: handlerResult?.content ?? content,
      metadata: { interactive: true, handlerManaged: true },
      interactive: true,
    };
  }

  // ── Generic InteractiveSession path (no registered handler) ──
  // Step 1 & 2: Create session and wait for user interaction
  const sessionResult = await createInteractiveSession(
    action,
    content,
    contentShape,
    metadata,
    context as InteractiveSession['context'],
  );

  // Step 3: Send the user-edited content to the backend for signal emission.
  // Even for interactive actions, the backend handler runs to emit
  // observability signals (spans, logs, metrics, retrieval events).
  // The content sent is the user-edited version, not the original.
  const response = await fetch(
    `${apiBase}/api/plugins/${action.pluginId}/actions/${action.name}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        contentShape,
        content: sessionResult.content,
        metadata: {
          ...metadata,
          interactiveSession: true,
          sessionApplied: sessionResult.applied,
        },
        context,
      }),
    },
  );

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(
      errData.error ?? errData.message ?? `Action "${action.label}" signal emission failed (${response.status})`,
    );
  }

  const backendResult = await response.json();

  // Step 4: Return the result — the content is the user-edited version
  // (the backend echoes it back after signal emission)
  return {
    outputShape: backendResult.outputShape ?? action.produces,
    content: backendResult.content ?? sessionResult.content,
    metadata: {
      ...backendResult.metadata,
      interactive: true,
      sessionApplied: sessionResult.applied,
    },
    interactive: true,
  };
}
